import { useCallback, useRef } from 'react';
import { rootContainer } from 'app/shadowRoot';
import useIsIntegratedView from 'app/hooks/useIsIntegratedView';
import { ensureElementVisible } from 'app/utils/ensureElementVisible';

import useLastReadMessageIndex from './useLastReadMessageIndex';
import useLastMessageIndex from './useLastMessageIndex';

export default function useScrollDown<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const { lastReadMessageIndex } = useLastReadMessageIndex();
  const lastMessageIndex = useLastMessageIndex();
  const isIntegratedView = useIsIntegratedView();

  const scrollToIndex = useCallback(
    (index: number) => {
      ref.current?.setAttribute('data-programmatic-scroll', 'true');

      setTimeout(() => {
        const messageElement = rootContainer.querySelector(`[data-message-index="${index}"]`);

        if (isIntegratedView) {
          ensureElementVisible(ref.current, messageElement as HTMLElement);
          return;
        }

        messageElement?.scrollIntoView({
          block: isIntegratedView ? 'nearest' : 'end',
        });
      });

      setTimeout(() => {
        ref.current?.removeAttribute('data-programmatic-scroll');
      }, 2000);
    },
    [isIntegratedView]
  );

  const scrollDown = useCallback(() => {
    const toIndex = lastMessageIndex > lastReadMessageIndex ? lastReadMessageIndex + 1 : lastReadMessageIndex;

    scrollToIndex(toIndex);
  }, [lastMessageIndex, lastReadMessageIndex, scrollToIndex]);

  return { ref, scrollDown, scrollToIndex };
}
